import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowsLeftRight,
  Camera,
  CaretLeft,
  CircleNotch,
  Export,
  ImagesSquare,
  Trash,
} from '@phosphor-icons/react'
import { Button } from '../../../components/ui/Button'
import { Badge } from '../../../components/ui/Badge'
import { Card } from '../../../components/ui/Card'
import { useAuth } from '@/core/AuthContext'
import {
  deleteProgressPhoto,
  exportAllPhotosToLibrary,
  exportPhotoToLibrary,
  formatProgressPhotoDate,
  getComparePair,
  getProgressPhotos,
} from '../api/progressPhotos'
import type { ProgressPhoto } from '../api/progressPhotos'

export function ProgressGalleryView() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [photos, setPhotos] = useState<ProgressPhoto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [exportingId, setExportingId] = useState<string | null>(null)
  const [isExportingAll, setIsExportingAll] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const showToast = useCallback((message: string) => {
    setToast(message)
    setTimeout(() => setToast(null), 2500)
  }, [])

  const loadPhotos = useCallback(async () => {
    if (!user) {
      setPhotos([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const loaded = await getProgressPhotos()
    setPhotos(loaded)
    setIsLoading(false)
  }, [user])

  useEffect(() => {
    void loadPhotos()
  }, [loadPhotos])

  const handleDelete = useCallback(
    async (photoId: string) => {
      await deleteProgressPhoto(photoId)
      setPhotos((prev) => prev.filter((p) => p.id !== photoId))
    },
    []
  )

  const handleExportOne = useCallback(
    async (photoId: string) => {
      setExportingId(photoId)
      try {
        await exportPhotoToLibrary(photoId)
        showToast('Saved to Photos')
      } catch {
        showToast('Failed to export')
      } finally {
        setExportingId(null)
      }
    },
    [showToast]
  )

  const handleExportAll = useCallback(async () => {
    setIsExportingAll(true)
    try {
      const count = await exportAllPhotosToLibrary()
      showToast(`Exported ${count} photo${count !== 1 ? 's' : ''} to Photos`)
    } catch {
      showToast('Failed to export')
    } finally {
      setIsExportingAll(false)
    }
  }, [showToast])

  const comparePair = getComparePair(photos)

  if (!user) {
    return null
  }

  return (
    <div className="min-h-screen">
      <div className="border-b border-gray-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-900 safe-top">
        <div className="mx-auto max-w-6xl">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/client/progress')}
            className="mb-2"
          >
            <CaretLeft size={16} weight="bold" />
            Back to Progress
          </Button>
          <Badge variant="info" size="sm" className="mb-1">
            Progress Photos
          </Badge>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            Your visual check-ins
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 rounded-[2rem] border border-gray-200/70 bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.16),_transparent_36%),linear-gradient(135deg,_rgba(255,255,255,0.94),_rgba(245,247,250,0.88))] p-6 shadow-xl shadow-orange-100/50 dark:border-white/10 dark:bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.18),_transparent_30%),linear-gradient(135deg,_rgba(17,24,39,0.96),_rgba(3,7,18,0.98))] dark:shadow-none">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary-600 dark:text-primary-400">
                Progress Photos
              </p>
              <h1 className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                Your visual check-ins
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-gray-600 dark:text-slate-300">
                Capture the same pose over time and keep the shots stored only on this device.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {photos.length > 0 && (
                <Button
                  variant="secondary"
                  onClick={() => void handleExportAll()}
                  disabled={isExportingAll}
                >
                  {isExportingAll ? (
                    <CircleNotch size={18} weight="bold" className="animate-spin" />
                  ) : (
                    <Export size={18} weight="bold" />
                  )}
                  Export All to Photos
                </Button>
              )}
              {comparePair && (
                <Button variant="secondary" onClick={() => navigate('/client/progress/photos/compare')}>
                  <ArrowsLeftRight size={18} weight="bold" />
                  Compare
                </Button>
              )}
              <Button onClick={() => navigate('/client/progress/photos/capture')}>
                <Camera size={18} weight="bold" />
                Take Progress Photo
              </Button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Card padding="md" className="rounded-2xl border border-white/50 bg-white/70 dark:border-white/10 dark:bg-white/5">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-400">Saved</p>
              <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">{photos.length}</p>
            </Card>
            <Card padding="md" className="rounded-2xl border border-white/50 bg-white/70 dark:border-white/10 dark:bg-white/5">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-400">First Check-in</p>
              <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                {photos.length > 0
                  ? formatProgressPhotoDate(photos[photos.length - 1].createdAt)
                  : 'None yet'}
              </p>
            </Card>
            <Card padding="md" className="rounded-2xl border border-white/50 bg-white/70 dark:border-white/10 dark:bg-white/5">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-400">Latest</p>
              <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                {photos.length > 0 ? formatProgressPhotoDate(photos[0].createdAt) : 'None yet'}
              </p>
            </Card>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <CircleNotch size={32} weight="bold" className="animate-spin text-orange-400" />
          </div>
        ) : photos.length === 0 ? (
          <Card className="rounded-[2rem] p-10 text-center">
            <div className="mx-auto flex max-w-md flex-col items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-slate-300">
                <ImagesSquare size={30} weight="duotone" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">No photos yet</h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
                  Take your first progress shot to start a consistent visual history.
                </p>
              </div>
              <Button onClick={() => navigate('/client/progress/photos/capture')}>
                <Camera size={18} weight="bold" />
                Take Progress Photo
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {photos.map((photo) => (
              <Card
                key={photo.id}
                padding="none"
                className="group overflow-hidden rounded-[1.5rem] border border-gray-200/80 bg-white/70 shadow-lg shadow-gray-200/40 dark:border-white/10 dark:bg-white/5 dark:shadow-none"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={photo.imageDataUrl}
                    alt={`Progress photo from ${formatProgressPhotoDate(photo.createdAt)}`}
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-3">
                    <p className="text-sm font-semibold text-white">
                      {formatProgressPhotoDate(photo.createdAt, { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => void handleExportOne(photo.id)}
                      disabled={exportingId === photo.id}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white transition hover:bg-black/70"
                      aria-label="Export to Photos"
                    >
                      {exportingId === photo.id ? (
                        <CircleNotch size={16} weight="bold" className="animate-spin" />
                      ) : (
                        <Export size={16} weight="bold" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(photo.id)}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white transition hover:bg-black/70"
                      aria-label="Delete progress photo"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-gray-900 px-5 py-2.5 text-sm font-medium text-white shadow-lg dark:bg-white dark:text-gray-900">
          {toast}
        </div>
      )}
    </div>
  )
}
